export type StudentTopicLike = {
    e180_TopicId?: string;
    e180_ParentTopicNames?: string[];
    e180_ChildTopics?: StudentTopicLike[];
};

export type YearTopicGroupSegment = {
    kind: 'group';
    label: string;
    startIndex: number;
    endIndex: number;
    topics: StudentTopicLike[];
};

export type YearTopicLeafSegment = {
    kind: 'topic';
    startIndex: number;
    endIndex: number;
    topics: StudentTopicLike[];
};

export type YearTopicSegment = YearTopicGroupSegment | YearTopicLeafSegment;

export function hasRealChildTopics(topic: StudentTopicLike | null | undefined): boolean {
    return Boolean(topic?.e180_ChildTopics && topic.e180_ChildTopics.length > 0);
}

export function getPrimaryParentName(topic: StudentTopicLike | null | undefined): string | null {
    const raw = topic?.e180_ParentTopicNames?.[0];
    if (raw == null) return null;
    const trimmed = String(raw).trim();
    return trimmed || null;
}

function isGroupableLeaf(topic: StudentTopicLike): boolean {
    return !hasRealChildTopics(topic) && getPrimaryParentName(topic) != null;
}

/**
 * Split flat year topics into contiguous segments for virtual folder display.
 * Group segments share the same ParentTopicNames[0]; topic segments are nested parents or ungrouped leaves.
 */
export function groupStudentTopicsByPrimaryParent(topics: StudentTopicLike[] | null | undefined): YearTopicSegment[] {
    if (!topics?.length) return [];

    const segments: YearTopicSegment[] = [];
    let currentGroup: YearTopicGroupSegment | null = null;

    const flushGroup = () => {
        if (currentGroup) {
            segments.push(currentGroup);
            currentGroup = null;
        }
    };

    const pushTopicSegment = (topic: StudentTopicLike, index: number) => {
        flushGroup();
        segments.push({
            kind: 'topic',
            startIndex: index,
            endIndex: index,
            topics: [topic]
        });
    };

    for (let index = 0; index < topics.length; index++) {
        const topic = topics[index];
        if (!topic) continue;

        if (hasRealChildTopics(topic)) {
            pushTopicSegment(topic, index);
            continue;
        }

        const primaryParent = getPrimaryParentName(topic);
        if (!primaryParent) {
            pushTopicSegment(topic, index);
            continue;
        }

        if (currentGroup && currentGroup.label === primaryParent) {
            currentGroup.topics.push(topic);
            currentGroup.endIndex = index;
        } else {
            flushGroup();
            currentGroup = {
                kind: 'group',
                label: primaryParent,
                startIndex: index,
                endIndex: index,
                topics: [topic]
            };
        }
    }

    flushGroup();

    return segments;
}

export function flattenSegmentsToTopics(segments: YearTopicSegment[]): StudentTopicLike[] {
    const out: StudentTopicLike[] = [];
    for (const segment of segments) {
        out.push(...segment.topics);
    }
    return out;
}

/** Move an entire segment block within a year's flat topic list. */
export function reorderSegmentsInYearTopics(
    flatTopics: StudentTopicLike[],
    segments: YearTopicSegment[],
    sourceSegIndex: number,
    destSegIndex: number
): StudentTopicLike[] {
    if (
        sourceSegIndex < 0 ||
        destSegIndex < 0 ||
        sourceSegIndex >= segments.length ||
        destSegIndex >= segments.length ||
        sourceSegIndex === destSegIndex
    ) {
        return flatTopics;
    }

    const nextSegments = [...segments];
    const [moved] = nextSegments.splice(sourceSegIndex, 1);
    nextSegments.splice(destSegIndex, 0, moved);
    return flattenSegmentsToTopics(nextSegments);
}

/** Reorder topics inside a single segment slice of the flat year list. */
export function reorderTopicsWithinSegment(
    flatTopics: StudentTopicLike[],
    segment: YearTopicSegment,
    sourceRelIndex: number,
    destRelIndex: number
): StudentTopicLike[] {
    const sliceLen = segment.endIndex - segment.startIndex + 1;
    if (
        sliceLen <= 1 ||
        sourceRelIndex < 0 ||
        destRelIndex < 0 ||
        sourceRelIndex >= sliceLen ||
        destRelIndex >= sliceLen ||
        sourceRelIndex === destRelIndex
    ) {
        return flatTopics;
    }

    const slice = flatTopics.slice(segment.startIndex, segment.endIndex + 1);
    const [moved] = slice.splice(sourceRelIndex, 1);
    slice.splice(destRelIndex, 0, moved);

    const next = [...flatTopics];
    next.splice(segment.startIndex, sliceLen, ...slice);
    return next;
}

export function parseStudentCurriculumSegmentDroppableId(
    droppableId: string
): { yearNodeId: string; runIndex: number } | null {
    const match = /^(.*)-(grp|seg)-(\d+)$/.exec(droppableId);
    if (!match) return null;
    const runIndex = parseInt(match[3], 10);
    if (Number.isNaN(runIndex)) return null;
    return { yearNodeId: match[1], runIndex };
}
